/**
 * NinjaOne device-pull timing.
 *
 * Aitryx Core owns the automatic pull. This module only describes the intervals
 * that are realistic for a full device-list pull, and when a stored pull is old
 * enough to treat as overdue. The portal must not invent a client-side timer:
 * the partner app is not open often enough for that to keep the device list fresh.
 */

/** Shortest pull Core should be asked to run. A full inventory is too heavy to poll faster. */
export const MIN_NINJA_SYNC_INTERVAL_HOURS = 6;

/**
 * Used when Core does not report an interval. A daily pull that has not
 * succeeded for a day and a half is overdue. Once Core reports an interval,
 * overdue is 1.5× that interval instead.
 */
export const DEVICE_SYNC_STALE_AFTER_MS = 36 * 60 * 60 * 1000;

export const NINJA_SYNC_INTERVAL_OPTIONS = [
  { hours: 6, label: 'Every 6 hours', detail: 'Four pulls a day. Shortest interval for a full device list.' },
  { hours: 12, label: 'Every 12 hours', detail: 'Twice a day.' },
  { hours: 24, label: 'Once a day', detail: 'The usual cadence for device inventory.' },
  { hours: 168, label: 'Once a week', detail: 'Enough when the device estate rarely changes.' },
] as const;

export type NinjaSyncIntervalHours = (typeof NINJA_SYNC_INTERVAL_OPTIONS)[number]['hours'];

export function isAllowedSyncInterval(hours: number | null | undefined): hours is NinjaSyncIntervalHours {
  return NINJA_SYNC_INTERVAL_OPTIONS.some((option) => option.hours === hours);
}

export function syncIntervalOption(hours: number | null | undefined) {
  return NINJA_SYNC_INTERVAL_OPTIONS.find((option) => option.hours === hours) ?? null;
}

/** True when this Core build includes the interval on the NinjaOne config payload. */
export function serverReportsSyncInterval(config: object | null | undefined): boolean {
  return !!config && Object.prototype.hasOwnProperty.call(config, 'syncIntervalHours');
}

export function deviceSyncStaleAfterMs(intervalHours?: number | null): number {
  if (intervalHours && intervalHours > 0) return intervalHours * 1.5 * 60 * 60 * 1000;
  return DEVICE_SYNC_STALE_AFTER_MS;
}

/**
 * Core writes device-pull times in UTC and often omits the zone (no Z, no offset).
 * A zone-less value parsed with `new Date` is treated as the browser's local time,
 * so a sync just completed in Sydney shows up about 11 hours old.
 * Values that already carry Z or an offset are left alone.
 */
export function normalizeApiInstant(iso: string): string {
  const trimmed = iso.trim();
  if (/(?:Z|[+-]\d{2}:?\d{2})$/i.test(trimmed)) return trimmed;
  if (/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(trimmed)) return `${trimmed.replace(' ', 'T')}Z`;
  return trimmed;
}

export function parseApiInstant(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const parsed = new Date(normalizeApiInstant(iso)).getTime();
  return Number.isNaN(parsed) ? null : parsed;
}

export function formatApiInstant(iso?: string | null): string {
  const parsed = parseApiInstant(iso);
  if (parsed == null) return '';
  return new Date(parsed).toLocaleString('en-AU', {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
}

/** The later of the supplied Core timestamps, keeping the original text of the winner. */
export function latestApiInstant(...values: Array<string | null | undefined>): string | null {
  let best: { iso: string; at: number } | null = null;
  for (const iso of values) {
    if (!iso) continue;
    const at = parseApiInstant(iso);
    if (at == null) continue;
    if (!best || at > best.at) best = { iso, at };
  }
  return best?.iso ?? null;
}

export function deviceSyncAgeMs(iso: string | null | undefined, now: number): number | null {
  const parsed = parseApiInstant(iso);
  if (parsed == null) return null;
  return now - parsed;
}

export function isDeviceSyncStale(
  iso: string | null | undefined,
  now: number,
  intervalHours?: number | null,
): boolean {
  const age = deviceSyncAgeMs(iso, now);
  if (age == null) return true;
  if (age < 0) return false;
  return age > deviceSyncStaleAfterMs(intervalHours);
}

export function devicePullSummary(
  deviceCount: number,
  organizationCount: number,
  devicesWithSerial?: number,
): string {
  const orgs = `${organizationCount} organization${organizationCount === 1 ? '' : 's'}`;
  const serials = devicesWithSerial == null ? '' : ` (${devicesWithSerial} with serials)`;
  return `Pulled ${deviceCount} devices${serials} across ${orgs}.`;
}

export function deviceSyncAgeLabel(iso: string | null | undefined, now: number): string {
  const age = deviceSyncAgeMs(iso, now);
  if (!iso) return 'Never';
  if (age == null) return 'Unknown';
  if (age < 60_000) return 'Just now';
  const minutes = Math.floor(age / 60_000);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}
